import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, Fragment } from "react";
import { Search, Users, Loader2, ChevronDown, ChevronUp, CalendarCheck } from "lucide-react";
import { adminApi, type CustomerResponse } from "@/lib/api";
import { formatNaira } from "@/data/venues";
import { Input } from "@/components/ui/input";

export const Route = createFileRoute("/admin/customers")({
  component: CustomersPage,
});

function CustomersPage() {
  const [q, setQ] = useState("");
  const [customers, setCustomers] = useState<CustomerResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    adminApi.customers({ pageSize: 100 })
      .then(res => setCustomers(res.data))
      .catch(err => setError(err instanceof Error ? err.message : "Failed to load customers"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = customers.filter(c => {
    const name = `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim().toLowerCase();
    return name.includes(q.toLowerCase()) || (c.email ?? "").toLowerCase().includes(q.toLowerCase());
  });

  return (
    <div className="mx-auto max-w-7xl space-y-5 p-4 sm:p-6 lg:p-8">
      <div>
        <h1 className="font-display text-2xl font-bold sm:text-3xl">Customers</h1>
        <p className="text-sm text-muted-foreground">View customer profiles, assist with bookings, and manage accounts.</p>
      </div>

      <div className="rounded-2xl border border-border/60 bg-card shadow-soft">
        <div className="border-b border-border/60 p-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="pl-9" />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading customers…
          </div>
        ) : error ? (
          <div className="py-20 text-center text-sm text-destructive">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Users className="mb-3 size-12 text-muted-foreground/30" />
            <p className="font-semibold text-muted-foreground">
              {customers.length === 0 ? "No customers yet" : "No customers match your search"}
            </p>
            <p className="mt-1 max-w-xs text-sm text-muted-foreground">
              {customers.length === 0
                ? "Registered customers will appear here once guests start booking on the platform."
                : "Try adjusting your search terms."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium sm:px-5">Customer</th>
                  <th className="px-4 py-3 font-medium sm:px-5">Location</th>
                  <th className="px-4 py-3 font-medium sm:px-5">Joined</th>
                  <th className="px-4 py-3 font-medium sm:px-5">Bookings</th>
                  <th className="px-4 py-3 font-medium sm:px-5">Total Spent</th>
                  <th className="px-4 py-3 sm:px-5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {filtered.map((c) => (
                  <Fragment key={c.id}>
                    <tr className="hover:bg-accent/30">
                      <td className="px-4 py-3 sm:px-5">
                        <div className="font-medium">{c.firstName ?? ""} {c.lastName ?? ""}</div>
                        <div className="text-xs text-muted-foreground">{c.email}</div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground sm:px-5">{c.location ?? "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground sm:px-5 text-xs">
                        {new Date(c.createdAt).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" })}
                      </td>
                      <td className="px-4 py-3 font-semibold sm:px-5">{c.bookingCount}</td>
                      <td className="px-4 py-3 font-semibold sm:px-5">{formatNaira(c.totalSpent)}</td>
                      <td className="px-4 py-3 sm:px-5">
                        {c.recentBookings.length > 0 && (
                          <button
                            onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                            className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                          >
                            {expanded === c.id ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
                            {expanded === c.id ? "Hide" : "Bookings"}
                          </button>
                        )}
                      </td>
                    </tr>
                    {expanded === c.id && c.recentBookings.length > 0 && (
                      <tr>
                        <td colSpan={6} className="bg-muted/20 px-4 py-3 sm:px-5">
                          <div className="space-y-2">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Recent Bookings</p>
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead className="text-left text-muted-foreground">
                                  <tr>
                                    <th className="pb-1 font-medium">Ref</th>
                                    <th className="pb-1 font-medium">Venue</th>
                                    <th className="pb-1 font-medium">Check-in</th>
                                    <th className="pb-1 font-medium">Duration</th>
                                    <th className="pb-1 font-medium">Amount</th>
                                    <th className="pb-1 font-medium">Status</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-border/40">
                                  {c.recentBookings.map(b => (
                                    <tr key={b.bookingId}>
                                      <td className="py-1.5 font-mono text-[10px]">{b.bookingRef}</td>
                                      <td className="py-1.5">{b.venueName}</td>
                                      <td className="py-1.5 text-muted-foreground">
                                        {new Date(b.startDate).toLocaleDateString("en-NG", { day: "2-digit", month: "short" })}
                                      </td>
                                      <td className="py-1.5 text-muted-foreground">{b.daysCount} day{b.daysCount !== 1 ? "s" : ""}</td>
                                      <td className="py-1.5 font-semibold">{formatNaira(b.totalAmount)}</td>
                                      <td className="py-1.5">
                                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                                          b.status.toLowerCase() === "confirmed" ? "bg-green-100 text-green-700"
                                            : b.status.toLowerCase() === "pending" ? "bg-amber-100 text-amber-700"
                                            : b.status.toLowerCase() === "cancelled" ? "bg-red-100 text-red-700"
                                            : "bg-muted text-muted-foreground"
                                        }`}>
                                          {b.status}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
